import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Bell, Headphones, BookOpen, CircleDot, Compass, Calendar, Shield } from 'lucide-react';

const FloatingMenu = ({
  notifOpen,
  audioOpen,
  duaOpen,
  tasbihOpen,
  qiblaOpen,
  calendarOpen,
  adminOpen
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const menuItems = [
    { icon: Bell, label: 'Njoftimet', onClick: notifOpen, color: 'bg-white text-gray-700 border border-gray-200', testId: 'notification-bell' },
    { icon: Headphones, label: 'Ligjërata', onClick: audioOpen, color: 'bg-violet-600 text-white', testId: 'audio-access-button' },
    { icon: BookOpen, label: 'Duatë', onClick: duaOpen, color: 'bg-emerald-600 text-white', testId: 'dua-access-button' },
    { icon: CircleDot, label: 'Tesbih', onClick: tasbihOpen, color: 'bg-orange-500 text-white', testId: 'tasbih-access-button' },
    { icon: Compass, label: 'Kibla', onClick: qiblaOpen, color: 'bg-blue-600 text-white', testId: 'qibla-access-button' },
    { icon: Calendar, label: 'Kalendar', onClick: calendarOpen, color: 'bg-purple-600 text-white', testId: 'calendar-access-button' },
    { icon: Shield, label: 'Admin', onClick: adminOpen, color: 'bg-primary text-white', testId: 'admin-access-button' },
  ];

  const handleItemClick = (onClick) => {
    onClick();
    setIsExpanded(false);
  };

  return (
    <>
      {/* Backdrop when menu is open on mobile */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/30 z-40 md:hidden"
            onClick={() => setIsExpanded(false)}
          />
        )}
      </AnimatePresence>

      {/* Desktop: Show all buttons vertically */}
      <div className="hidden md:flex fixed bottom-6 right-6 flex-col gap-3 z-40">
        {menuItems.map((item, index) => (
          <motion.button
            key={item.testId}
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: index * 0.05 }}
            onClick={() => item.onClick()}
            className={`w-14 h-14 ${item.color} rounded-full shadow-2xl flex items-center justify-center transition-all hover:scale-110`}
            data-testid={item.testId}
            title={item.label}
          >
            <item.icon className="w-6 h-6" />
          </motion.button>
        ))}
      </div>

      {/* Mobile: Expandable FAB menu */}
      <div className="md:hidden fixed bottom-6 right-6 z-40">
        <AnimatePresence>
          {isExpanded && (
            <motion.div
              initial={{ opacity: 0, scale: 0.8, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.8, y: 20 }}
              className="absolute bottom-16 right-0 bg-white rounded-2xl shadow-2xl p-3 min-w-[200px]"
            >
              <div className="grid grid-cols-3 gap-3">
                {menuItems.map((item, index) => (
                  <motion.button
                    key={item.testId}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.03 }}
                    onClick={() => handleItemClick(item.onClick)}
                    className="flex flex-col items-center gap-1 p-2 rounded-xl hover:bg-gray-100 active:bg-gray-200 transition-colors"
                    data-testid={item.testId}
                  >
                    <div className={`w-12 h-12 ${item.color} rounded-full flex items-center justify-center shadow-lg`}>
                      <item.icon className="w-5 h-5" />
                    </div>
                    <span className="text-[10px] font-medium text-gray-600 text-center leading-tight">
                      {item.label}
                    </span>
                  </motion.button>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Main FAB Toggle Button */}
        <motion.button
          onClick={() => setIsExpanded(!isExpanded)}
          className={`w-14 h-14 rounded-full shadow-2xl flex items-center justify-center transition-all ${
            isExpanded ? 'bg-gray-800 text-white' : 'bg-primary text-white'
          }`}
          whileTap={{ scale: 0.95 }}
          data-testid="floating-menu-toggle"
        >
          <motion.div
            animate={{ rotate: isExpanded ? 180 : 0 }}
            transition={{ duration: 0.2 }}
          >
            {isExpanded ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </motion.div>
        </motion.button>
      </div>
    </>
  );
};

export default FloatingMenu;
